import { useEffect, useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronLeft, Save, Lock, Upload, X, Image, Plus, Smartphone, Tablet, Apple } from 'lucide-react'
import axios from 'axios'
import { buildApiUrl } from '../../../config/api'
import { toast } from '../../../components/Toast'
import { Button, Card, Input, Select, Spinner, Tabs, Textarea } from '../../../components/ui'

interface FormState {
  app_name: string
  short_name: string
  bundle_id: string
  subtitle: string
  short_description: string
  long_description: string
  keywords: string
  store_category: string
  primary_color: string
  secondary_color: string
  accent_color: string
  background_color: string
  font_family: string
  icon_1024_url: string
  splash_url: string
  feature_graphic_url: string
  cover_banner_url: string
  privacy_url: string
  terms_url: string
  support_email: string
  support_phone: string
  demo_identifier: string
  demo_password: string
}

const initialState: FormState = {
  app_name: '',
  short_name: '',
  bundle_id: '',
  subtitle: '',
  short_description: '',
  long_description: '',
  keywords: '',
  store_category: 'SOCIAL_NETWORKING',
  primary_color: '#465fff',
  secondary_color: '',
  accent_color: '',
  background_color: '#FFFFFF',
  font_family: 'Inter',
  icon_1024_url: '',
  splash_url: '',
  feature_graphic_url: '',
  cover_banner_url: '',
  privacy_url: '',
  terms_url: '',
  support_email: '',
  support_phone: '',
  demo_identifier: '',
  demo_password: '',
}

const categoryOptions = [
  { value: 'SOCIAL_NETWORKING', label: 'Redes Sociais' },
  { value: 'NEWS', label: 'Notícias' },
  { value: 'GOVERNMENT', label: 'Governo' },
  { value: 'LIFESTYLE', label: 'Estilo de Vida' },
  { value: 'REFERENCE', label: 'Referência' },
]

export default function AppConfigFormPage() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState<FormState>(initialState)
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({})

  useEffect(() => {
    fetchConfig()
  }, [])

  async function fetchConfig() {
    setLoading(true)
    try {
      const res = await axios.get(buildApiUrl('/v1/app-config'))
      const data = res.data.data
      if (!data) {
        navigate('/settings/app')
        return
      }
      setForm({
        app_name: data.app_name ?? '',
        short_name: data.short_name ?? '',
        bundle_id: data.bundle_id ?? '',
        subtitle: data.subtitle ?? '',
        short_description: data.short_description ?? '',
        long_description: data.long_description ?? '',
        keywords: data.keywords ?? '',
        store_category: data.store_category ?? 'SOCIAL_NETWORKING',
        primary_color: data.primary_color ?? '#465fff',
        secondary_color: data.secondary_color ?? '',
        accent_color: data.accent_color ?? '',
        background_color: data.background_color ?? '#FFFFFF',
        font_family: data.font_family ?? 'Inter',
        icon_1024_url: data.icon_1024_url ?? '',
        splash_url: data.splash_url ?? '',
        feature_graphic_url: data.feature_graphic_url ?? '',
        cover_banner_url: data.cover_banner_url ?? '',
        privacy_url: data.privacy_url ?? '',
        terms_url: data.terms_url ?? '',
        support_email: data.support_email ?? '',
        support_phone: data.support_phone ?? '',
        demo_identifier: data.demo_identifier ?? '',
        demo_password: data.demo_password ?? '',
      })
    } catch (err) {
      console.error('Erro ao carregar', err)
      toast('error', 'Não foi possível carregar a configuração')
    } finally {
      setLoading(false)
    }
  }

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm(prev => ({ ...prev, [key]: value }))
    if (errors[key]) setErrors(prev => { const n = { ...prev }; delete n[key]; return n })
  }

  function validate(): boolean {
    const e: Partial<Record<keyof FormState, string>> = {}

    if (!form.app_name.trim()) e.app_name = 'Nome do app é obrigatório'
    else if (form.app_name.length > 30) e.app_name = 'Máximo 30 caracteres'

    if (form.short_name && form.short_name.length > 12) e.short_name = 'Máximo 12 caracteres'

    if (form.subtitle && form.subtitle.length > 30) e.subtitle = 'Máximo 30 caracteres'

    if (form.short_description && form.short_description.length > 80) e.short_description = 'Máximo 80 caracteres'

    if (form.keywords && form.keywords.length > 100) e.keywords = 'Máximo 100 caracteres'

    if (!form.primary_color) e.primary_color = 'Cor primária é obrigatória'
    else if (!/^#[0-9A-Fa-f]{6}$/.test(form.primary_color)) e.primary_color = 'Formato inválido (ex: #465fff)'

    if (form.support_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.support_email)) e.support_email = 'E-mail inválido'

    if (form.support_phone && form.support_phone.length !== 11) e.support_phone = 'Telefone deve ter 11 dígitos'

    if (form.privacy_url && !/^https?:\/\/.+/.test(form.privacy_url)) e.privacy_url = 'URL inválida'
    if (form.terms_url && !/^https?:\/\/.+/.test(form.terms_url)) e.terms_url = 'URL inválida'

    setErrors(e)

    if (Object.keys(e).length > 0) {
      toast('error', 'Corrija os campos em vermelho')
      // Scroll pro primeiro erro
      const firstKey = Object.keys(e)[0]
      const el = document.querySelector(`[data-field="${firstKey}"]`)
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }

    return Object.keys(e).length === 0
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (saving) return
    if (!validate()) return

    setSaving(true)
    try {
      const { bundle_id: _bundleId, ...payload } = form
      await axios.put(buildApiUrl('/v1/app-config'), payload)
      toast('success', 'App atualizado')
      navigate('/settings/app')
    } catch (err: any) {
      const message = err?.response?.data?.message || 'Erro ao salvar'
      // Se a API retorna erros por campo
      if (err?.response?.data?.errors) {
        const apiErrors: Record<string, string[]> = err.response.data.errors
        const mapped: Partial<Record<keyof FormState, string>> = {}
        Object.entries(apiErrors).forEach(([key, msgs]) => {
          mapped[key as keyof FormState] = msgs[0]
        })
        setErrors(mapped)
      }
      toast('error', message)
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Spinner />
      </div>
    )
  }

  const title = 'Editar App'

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => navigate('/settings/app')}
          className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          aria-label="Voltar"
        >
          <ChevronLeft className="w-5 h-5 text-gray-600" />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
          <p className="text-gray-500 mt-1">Configure identidade, branding e metadados do app mobile</p>
        </div>
      </div>

      <Card className="p-6 space-y-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Identidade na loja</h2>
          <p className="text-sm text-gray-500 mt-0.5">Como o app aparece na App Store e Google Play</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div data-field="app_name">
            <Input
              label="Nome do app *"
              value={form.app_name}
              onChange={e => update('app_name', e.target.value)}
              maxLength={30}
              placeholder="Ex: João Vereador"
              hint={errors.app_name || 'Aparece abaixo do ícone. Máx 30 chars.'}
              error={errors.app_name}
            />
          </div>
          <Input
            label="Bundle ID"
            value={form.bundle_id}
            readOnly
            disabled
            tabIndex={-1}
            iconRight={<Lock className="w-4 h-4" />}
            className="bg-gray-50 text-gray-500 cursor-not-allowed font-mono"
            hint="Gerado automaticamente do slug da organização. Não pode ser alterado."
          />
          <div data-field="short_name">
            <Input
              label="Nome curto"
              value={form.short_name}
              onChange={e => update('short_name', e.target.value)}
              maxLength={12}
              placeholder="Ex: João V."
              hint={errors.short_name || 'Fallback Android. Máx 12 chars.'}
              error={errors.short_name}
            />
          </div>
          <div data-field="subtitle">
            <Input
              label="Subtítulo"
              value={form.subtitle}
              onChange={e => update('subtitle', e.target.value)}
              maxLength={30}
              placeholder="Ex: Participe da campanha"
              hint={errors.subtitle || 'Subtítulo na App Store.'}
              error={errors.subtitle}
            />
          </div>
          <div data-field="short_description">
            <Input
              label="Descrição curta"
              value={form.short_description}
              onChange={e => update('short_description', e.target.value)}
              maxLength={80}
              placeholder="Breve descrição para Play Store"
              hint={errors.short_description || 'Máx 80 chars.'}
              error={errors.short_description}
            />
          </div>
          <Select
            label="Categoria"
            value={form.store_category}
            onChange={e => update('store_category', e.target.value)}
            options={categoryOptions}
          />
        </div>

        <Textarea
          label="Descrição longa"
          value={form.long_description}
          onChange={e => update('long_description', e.target.value)}
          rows={5}
          maxLength={4000}
          placeholder="Descrição completa exibida na página do app"
          hint="Máx 4000 chars."
        />

        <div data-field="keywords">
          <Input
            label="Palavras-chave (iOS)"
            value={form.keywords}
            onChange={e => update('keywords', e.target.value)}
            maxLength={100}
            placeholder="vereador, recife, política, campanha"
            hint={errors.keywords || 'Separadas por vírgula. Máx 100 chars. Só afeta a App Store.'}
            error={errors.keywords}
          />
        </div>
      </Card>

      <Card className="p-6 space-y-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Branding</h2>
          <p className="text-sm text-gray-500 mt-0.5">Cores e tipografia dentro do app</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Cor primária *"
            type="color"
            value={form.primary_color}
            onChange={e => update('primary_color', e.target.value)}
            required
          />
          <Input
            label="Cor secundária"
            type="color"
            value={form.secondary_color || '#000000'}
            onChange={e => update('secondary_color', e.target.value)}
          />
          <Input
            label="Cor de destaque"
            type="color"
            value={form.accent_color || '#000000'}
            onChange={e => update('accent_color', e.target.value)}
          />
          <Input
            label="Cor de fundo"
            type="color"
            value={form.background_color || '#FFFFFF'}
            onChange={e => update('background_color', e.target.value)}
          />
          <FontSelect
            value={form.font_family}
            onChange={v => update('font_family', v)}
          />
        </div>
      </Card>

      <Card className="p-6 space-y-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Assets</h2>
          <p className="text-sm text-gray-500 mt-0.5">Imagens necessárias para publicar nas lojas e renderizar no app</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <AssetField
            label="Ícone 1024×1024"
            required
            field="icon_1024_url"
            value={form.icon_1024_url}
            onChange={url => update('icon_1024_url', url)}
            aspect="square"
            specs="PNG 1024×1024, sem transparência, sem bordas arredondadas."
          />
          <AssetField
            label="Splash screen"
            field="splash_url"
            value={form.splash_url}
            onChange={url => update('splash_url', url)}
            aspect="square"
            specs="PNG 2048×2048 com logo centralizado."
          />
          <AssetField
            label="Feature graphic (Play Store)"
            field="feature_graphic_url"
            value={form.feature_graphic_url}
            onChange={url => update('feature_graphic_url', url)}
            aspect="wide"
            specs="1024×500, banner exibido no topo da página Play Store."
          />
          <AssetField
            label="Banner de capa"
            field="cover_banner_url"
            value={form.cover_banner_url}
            onChange={url => update('cover_banner_url', url)}
            aspect="wide"
            specs="1500×500, exibido dentro do app."
          />
        </div>
      </Card>

      <ScreenshotsSection />

      <Card className="p-6 space-y-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Legal e suporte</h2>
          <p className="text-sm text-gray-500 mt-0.5">Obrigatórios para publicar nas lojas</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div data-field="privacy_url">
            <Input
              label="URL política de privacidade"
              value={form.privacy_url}
              onChange={e => update('privacy_url', e.target.value)}
              placeholder="https://..."
              hint={errors.privacy_url || 'Se vazio, usamos o padrão do SisPoli.'}
              error={errors.privacy_url}
            />
          </div>
          <div data-field="terms_url">
            <Input
              label="URL termos de uso"
              value={form.terms_url}
              onChange={e => update('terms_url', e.target.value)}
              placeholder="https://..."
              hint={errors.terms_url}
              error={errors.terms_url}
            />
          </div>
          <div data-field="support_email">
            <Input
              label="E-mail de suporte *"
              type="email"
              value={form.support_email}
              onChange={e => update('support_email', e.target.value)}
              placeholder="suporte@exemplo.com"
              hint={errors.support_email}
              error={errors.support_email}
            />
          </div>
          <div data-field="support_phone">
            <Input
              label="Telefone de suporte"
              value={form.support_phone}
              onChange={e => update('support_phone', e.target.value.replace(/\D/g, '').slice(0, 11))}
              placeholder="11999999999"
              hint={errors.support_phone}
              error={errors.support_phone}
            />
          </div>
        </div>

        <div className="mt-6 p-4 rounded-xl bg-amber-50 border border-amber-200">
          <h3 className="font-semibold text-amber-900 mb-1">Conta de teste para revisão</h3>
          <p className="text-sm text-amber-800 mb-4">
            Credenciais que os revisores da Apple e Google vão usar pra logar no app durante a análise.
            Crie previamente este usuário na comunidade e informe aqui.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Telefone ou e-mail de teste"
              value={form.demo_identifier}
              onChange={e => update('demo_identifier', e.target.value)}
              placeholder="82996775045"
              hint="Login que o revisor vai usar (o mesmo campo 'identifier' do login da comunidade)"
            />
            <Input
              label="Senha de teste"
              type="text"
              value={form.demo_password}
              onChange={e => update('demo_password', e.target.value)}
              placeholder="••••••"
              hint="Senha em texto — só o revisor vai ver via App Store Connect"
            />
          </div>
        </div>
      </Card>

      <div className="flex justify-end">
        <Button type="submit" loading={saving}>
          <Save className="w-4 h-4 mr-2" />
          Salvar
        </Button>
      </div>
    </form>
  )
}

type AssetFieldProps = {
  label: string
  required?: boolean
  field: 'icon_1024_url' | 'splash_url' | 'feature_graphic_url' | 'cover_banner_url'
  value: string
  onChange: (url: string) => void
  aspect: 'square' | 'wide'
  specs?: string
}

function AssetField({ label, required, field, value, onChange, aspect, specs }: AssetFieldProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  const [removing, setRemoving] = useState(false)

  async function handleFile(file: File) {
    if (uploading) return
    setUploading(true)
    try {
      const fd = new FormData()
      fd.append('file', file)
      fd.append('field', field)
      const res = await axios.post(buildApiUrl('/v1/app-config/upload'), fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      onChange(res.data.url)
      toast('success', 'Imagem enviada')
    } catch (err: any) {
      const message = err?.response?.data?.message || 'Erro ao enviar imagem'
      toast('error', message)
    } finally {
      setUploading(false)
      if (inputRef.current) inputRef.current.value = ''
    }
  }

  async function handleRemove() {
    if (removing) return
    setRemoving(true)
    try {
      await axios.delete(buildApiUrl('/v1/app-config/upload'), {
        data: { field },
      })
      onChange('')
    } catch (err: any) {
      const message = err?.response?.data?.message || 'Erro ao remover'
      toast('error', message)
    } finally {
      setRemoving(false)
    }
  }

  const aspectClass = aspect === 'square' ? 'aspect-square' : 'aspect-[1024/500]'
  const busy = uploading || removing

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1.5">
        {label} {required && <span className="text-error-500">*</span>}
      </label>
      <div
        className={`relative ${aspectClass} w-full rounded-xl border-2 border-dashed border-gray-300 bg-gray-25 overflow-hidden flex items-center justify-center`}
      >
        {value ? (
          <>
            <img src={value} alt={label} className="w-full h-full object-contain" />
            <div className="absolute inset-0 bg-black/0 hover:bg-black/40 transition-colors flex items-center justify-center opacity-0 hover:opacity-100">
              <div className="flex gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  onClick={() => inputRef.current?.click()}
                  loading={uploading}
                  disabled={busy}
                >
                  <Upload className="w-4 h-4 mr-1.5" />
                  Trocar
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="danger"
                  onClick={handleRemove}
                  loading={removing}
                  disabled={busy}
                >
                  <X className="w-4 h-4 mr-1.5" />
                  Remover
                </Button>
              </div>
            </div>
          </>
        ) : (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={busy}
            className="flex flex-col items-center justify-center w-full h-full text-gray-400 hover:text-brand-500 hover:bg-brand-25 transition-colors"
          >
            {uploading ? (
              <Spinner />
            ) : (
              <>
                <Image className="w-8 h-8 mb-2" />
                <span className="text-sm font-medium">Clique para enviar</span>
              </>
            )}
          </button>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={e => {
          const file = e.target.files?.[0]
          if (file) handleFile(file)
        }}
      />
      {specs && <p className="mt-1.5 text-xs text-gray-500">{specs}</p>}
    </div>
  )
}

// ── Screenshots da loja ────────────────────────────────────

type Platform = 'IOS_APP' | 'ANDROID_APP'
type DeviceType = 'IPHONE_67' | 'IPHONE_65' | 'IPAD_13' | 'PHONE' | 'TABLET_7' | 'TABLET_10'

interface Screenshot {
  id: number
  platform_type_id: number
  platform_code: Platform
  device_type: DeviceType
  display_order: number
  image_url: string
  caption: string | null
}

interface DeviceGroup {
  platform: Platform
  device_type: DeviceType
  label: string
  specs: string
  required: boolean
  icon: typeof Smartphone
}

const deviceGroups: DeviceGroup[] = [
  { platform: 'IOS_APP',     device_type: 'IPHONE_67', label: 'iPhone 6.7"',   specs: '1290×2796 — iPhone 15/16 Pro Max, 14/15/16 Plus', required: true,  icon: Apple },
  { platform: 'ANDROID_APP', device_type: 'PHONE',     label: 'Android Phone', specs: '1080×1920 (mín) — qualquer Android moderno',      required: true,  icon: Smartphone },
  { platform: 'IOS_APP',     device_type: 'IPAD_13',   label: 'iPad 13"',      specs: '2048×2732 — obrigatório só se o app roda em iPad', required: false, icon: Tablet },
  { platform: 'ANDROID_APP', device_type: 'TABLET_7',  label: 'Android 7"',    specs: 'Tablet 7 polegadas — só se o app suporta tablet',  required: false, icon: Tablet },
  { platform: 'ANDROID_APP', device_type: 'TABLET_10', label: 'Android 10"',   specs: 'Tablet 10 polegadas — só se o app suporta tablet', required: false, icon: Tablet },
]

function ScreenshotsSection() {
  const [screenshots, setScreenshots] = useState<Screenshot[]>([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState<DeviceType>('IPHONE_67')
  const [extraVisible, setExtraVisible] = useState<Set<DeviceType>>(new Set())
  const [menuOpen, setMenuOpen] = useState(false)

  useEffect(() => {
    fetchScreenshots()
  }, [])

  async function fetchScreenshots() {
    setLoading(true)
    try {
      const res = await axios.get(buildApiUrl('/v1/app-screenshots'))
      const rows: Screenshot[] = res.data.data ?? []
      setScreenshots(rows)
      // Revela opcionais que já têm screenshots persistidos
      const withShots = new Set(rows.map(s => s.device_type))
      setExtraVisible(prev => {
        const next = new Set(prev)
        deviceGroups.forEach(g => {
          if (!g.required && withShots.has(g.device_type)) next.add(g.device_type)
        })
        return next
      })
    } catch (err) {
      console.error('Erro ao carregar screenshots', err)
    } finally {
      setLoading(false)
    }
  }

  const visibleGroups = deviceGroups.filter(g => g.required || extraVisible.has(g.device_type))
  const hiddenGroups = deviceGroups.filter(g => !g.required && !extraVisible.has(g.device_type))
  const activeGroup = visibleGroups.find(g => g.device_type === activeTab) ?? visibleGroups[0]

  const tabs = visibleGroups.map(g => ({
    label: g.label,
    value: g.device_type,
    count: screenshots.filter(s => s.device_type === g.device_type).length,
  }))

  function revealGroup(deviceType: DeviceType) {
    setExtraVisible(prev => {
      const next = new Set(prev)
      next.add(deviceType)
      return next
    })
    setActiveTab(deviceType)
    setMenuOpen(false)
  }

  function hideGroup(deviceType: DeviceType) {
    setExtraVisible(prev => {
      const next = new Set(prev)
      next.delete(deviceType)
      return next
    })
    if (activeTab === deviceType) {
      setActiveTab(visibleGroups.find(g => g.device_type !== deviceType)?.device_type ?? 'IPHONE_67')
    }
  }

  const allTabs = deviceGroups.map(g => {
    const count = screenshots.filter(s => s.device_type === g.device_type).length
    return {
      label: `${g.label} ${g.required ? '*' : ''}`,
      value: g.device_type,
      count,
    }
  })

  const currentGroup = deviceGroups.find(g => g.device_type === activeTab) ?? deviceGroups[0]

  return (
    <Card className="p-6 space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">Screenshots da loja</h2>
        <p className="text-sm text-gray-500 mt-0.5">Exibidos na página do app. Apple e Google reescalam para dispositivos menores automaticamente.</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Spinner />
        </div>
      ) : (
        <>
          {/* Abas por dispositivo */}
          <div className="flex gap-1 overflow-x-auto border-b border-gray-200">
            {allTabs.map(tab => {
              const isActive = tab.value === activeTab
              return (
                <button
                  key={tab.value}
                  type="button"
                  onClick={() => setActiveTab(tab.value as DeviceType)}
                  className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                    isActive
                      ? 'border-brand-500 text-brand-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab.label}
                  <span className={`min-w-[20px] h-5 px-1.5 rounded-full text-xs font-semibold flex items-center justify-center ${
                    isActive ? 'bg-brand-50 text-brand-600' : 'bg-gray-100 text-gray-500'
                  }`}>
                    {tab.count}
                  </span>
                </button>
              )
            })}
          </div>

          {currentGroup && (
            <DeviceScreenshotGroup
              key={`${currentGroup.platform}-${currentGroup.device_type}`}
              group={currentGroup}
              items={screenshots.filter(s => s.platform_code === currentGroup.platform && s.device_type === currentGroup.device_type)}
              onChange={fetchScreenshots}
            />
          )}
        </>
      )}
    </Card>
  )
}

interface DeviceScreenshotGroupProps {
  group: DeviceGroup
  items: Screenshot[]
  onChange: () => void
}

function DeviceScreenshotGroup({ group, items, onChange }: DeviceScreenshotGroupProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  const [removingId, setRemovingId] = useState<number | null>(null)
  const [dragId, setDragId] = useState<number | null>(null)
  const [overId, setOverId] = useState<number | null>(null)
  const Icon = group.icon

  async function persistOrder(orderedIds: number[]) {
    try {
      await axios.put(buildApiUrl('/v1/app-screenshots/reorder'), { ids: orderedIds })
      onChange()
    } catch (err: any) {
      const message = err?.response?.data?.message || 'Erro ao reordenar'
      toast('error', message)
      onChange()
    }
  }

  function handleDrop(targetId: number) {
    if (dragId === null || dragId === targetId) {
      setDragId(null)
      setOverId(null)
      return
    }
    const currentIds = items.map(i => i.id)
    const from = currentIds.indexOf(dragId)
    const to = currentIds.indexOf(targetId)
    if (from === -1 || to === -1) {
      setDragId(null)
      setOverId(null)
      return
    }
    const reordered = [...currentIds]
    const [moved] = reordered.splice(from, 1)
    reordered.splice(to, 0, moved)
    setDragId(null)
    setOverId(null)
    persistOrder(reordered)
  }

  async function handleFiles(files: FileList | null) {
    if (!files || uploading) return
    setUploading(true)
    try {
      for (const file of Array.from(files)) {
        const fd = new FormData()
        fd.append('file', file)
        fd.append('platform_code', group.platform)
        fd.append('device_type', group.device_type)
        await axios.post(buildApiUrl('/v1/app-screenshots'), fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
      }
      toast('success', files.length > 1 ? `${files.length} screenshots adicionados` : 'Screenshot adicionado')
      onChange()
    } catch (err: any) {
      const message = err?.response?.data?.message || 'Erro ao enviar screenshot'
      toast('error', message)
    } finally {
      setUploading(false)
      if (inputRef.current) inputRef.current.value = ''
    }
  }

  async function handleRemove(id: number) {
    if (removingId) return
    setRemovingId(id)
    try {
      await axios.delete(buildApiUrl(`/v1/app-screenshots/${id}`))
      onChange()
    } catch (err: any) {
      const message = err?.response?.data?.message || 'Erro ao remover'
      toast('error', message)
    } finally {
      setRemovingId(null)
    }
  }

  const meetsMinimum = !group.required || items.length >= 3

  return (
    <div>
      <div className="flex items-start justify-between mb-3 gap-3">
        <div className="flex items-start gap-3 flex-1">
          <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center text-gray-600 flex-shrink-0">
            <Icon className="w-5 h-5" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-gray-800">{group.label}</h3>
              {group.required && (
                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                  meetsMinimum ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'
                }`}>
                  {meetsMinimum ? `${items.length}/mín 3` : `${items.length}/3 — faltam ${3 - items.length}`}
                </span>
              )}
              {!group.required && items.length > 0 && (
                <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                  {items.length}
                </span>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-0.5">{group.specs}</p>
            {items.length > 1 && (
              <p className="text-xs text-gray-400 mt-0.5">Arraste para reordenar.</p>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
        {items.map((item, idx) => (
          <div
            key={item.id}
            draggable
            onDragStart={() => setDragId(item.id)}
            onDragOver={e => { e.preventDefault(); if (overId !== item.id) setOverId(item.id) }}
            onDragLeave={() => { if (overId === item.id) setOverId(null) }}
            onDrop={e => { e.preventDefault(); handleDrop(item.id) }}
            onDragEnd={() => { setDragId(null); setOverId(null) }}
            className={`relative aspect-[9/16] rounded-lg border overflow-hidden bg-gray-50 group cursor-move transition-all ${
              dragId === item.id ? 'opacity-40' : ''
            } ${
              overId === item.id && dragId !== item.id
                ? 'border-brand-500 ring-2 ring-brand-500/30'
                : 'border-gray-200'
            }`}
          >
            <img src={item.image_url} alt="" className="w-full h-full object-cover pointer-events-none" />
            <span className="absolute top-1.5 left-1.5 min-w-[20px] h-5 px-1.5 rounded-full bg-black/60 text-white text-[10px] font-semibold flex items-center justify-center">
              {idx + 1}
            </span>
            <button
              type="button"
              onClick={() => handleRemove(item.id)}
              disabled={removingId === item.id}
              className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-black/60 hover:bg-error-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-100"
              aria-label="Remover"
            >
              {removingId === item.id ? <Spinner /> : <X className="w-3.5 h-3.5" />}
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="aspect-[9/16] rounded-lg border-2 border-dashed border-gray-300 bg-gray-25 hover:border-brand-400 hover:bg-brand-25 text-gray-400 hover:text-brand-500 transition-colors flex flex-col items-center justify-center"
        >
          {uploading ? <Spinner /> : (
            <>
              <Plus className="w-5 h-5 mb-1" />
              <span className="text-xs font-medium">Adicionar</span>
            </>
          )}
        </button>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={e => handleFiles(e.target.files)}
      />
    </div>
  )
}

// ── Font Select com preview ────────────────────────────────

interface FontOption {
  id: number
  name: string
  family: string
  google_fonts_url: string | null
  preview_text: string
}

function FontSelect({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [fonts, setFonts] = useState<FontOption[]>([])
  const [open, setOpen] = useState(false)
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    axios.get(buildApiUrl('/v1/fonts'))
      .then(res => {
        const data: FontOption[] = res.data.data ?? []
        setFonts(data)
        // Carregar todas as fontes do Google Fonts
        data.forEach(f => {
          if (f.google_fonts_url) {
            const link = document.createElement('link')
            link.rel = 'stylesheet'
            link.href = f.google_fonts_url
            document.head.appendChild(link)
          }
        })
        setLoaded(true)
      })
      .catch(() => setLoaded(true))
  }, [])

  const selected = fonts.find(f => f.family === value)

  if (!loaded) return <div className="h-11 bg-gray-50 rounded-lg animate-pulse" />

  return (
    <div className="relative">
      <label className="block text-sm font-medium text-gray-700 mb-1.5">Fonte</label>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="h-11 w-full px-3 border border-gray-300 rounded-lg text-sm text-left bg-white hover:border-gray-400 transition-colors flex items-center justify-between"
      >
        <span style={{ fontFamily: value }}>{selected?.name || value || 'Selecione'}</span>
        <ChevronLeft className={`w-4 h-4 text-gray-400 transition-transform ${open ? 'rotate-90' : '-rotate-90'}`} />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg max-h-64 overflow-y-auto">
            {fonts.map(f => (
              <button
                key={f.id}
                type="button"
                onClick={() => { onChange(f.family); setOpen(false) }}
                className={`w-full px-3 py-2.5 text-left hover:bg-gray-50 transition-colors flex items-center justify-between ${
                  f.family === value ? 'bg-brand-25' : ''
                }`}
              >
                <div>
                  <p className="text-sm font-medium text-gray-800" style={{ fontFamily: f.family }}>{f.name}</p>
                  <p className="text-xs text-gray-400" style={{ fontFamily: f.family }}>{f.preview_text}</p>
                </div>
                {f.family === value && (
                  <span className="w-2 h-2 rounded-full bg-brand-500" />
                )}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  )
}
